import { Switch } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-pro";

import CoAppDataGridDateTimeCell from "../../global/components/datagrid/CoAppDataGridDateTimeCell";
import CoAppDataGridListCell from "../../global/components/datagrid/CoAppDataGridListCell";
import CoAppInlineTagsCell, { type InlineTag } from "../../global/components/datagrid/CoAppInlineTagsCell";
import RowActionIcons from "../../global/components/RowActionIcons";
import RuleDataGridLongTextCell from "../RuleDataGridLongTextCell";

/** Row fields read by rule library column definitions. */
export interface RuleLibraryGridRow {
	id?: number | string;
	uuid?: number | string;
	name?: string | null;
	active?: boolean;
	isDraft?: boolean;
	platform?: { name?: string } | null;
	description?: string | null;
	groups?: unknown;
	tags?: InlineTag[] | null;
	createdAt?: string | Date | null;
	updatedAt?: string | Date | null;
	lastCompiled?: string | Date | null;
	props?: Record<string, unknown>;
}

export const arrayColumns = ["groups", "tags"];
export const ruleLibraryColumns = ["active", "name", "description", "platform", "groups", "tags", "createdAt", "updatedAt", "lastCompiled", "actions"];

export const getRuleLibraryColumns = (
	handleStatusToggleChange: (rule: RuleLibraryGridRow) => void,
	handleEditRuleClick: (ruleId: number | string) => void,
	handleCopyRuleClick: (ruleId: number | string) => void,
	handleDeleteRuleToggle: (rule?: RuleLibraryGridRow) => void,
	handleAddTagToRule?: (rule: RuleLibraryGridRow, tag: InlineTag) => void,
	handleRemoveTagFromRule?: (rule: RuleLibraryGridRow, tag: InlineTag) => void
): GridColDef<RuleLibraryGridRow>[] => [
	{
		field: "active",
		headerName: "Status",
		width: 90,
		align: "center",
		headerAlign: "center",
		sortable: true,
		renderCell: (params) => (
			<Switch
				checked={Boolean(params.value)}
				disabled={params.row.isDraft}
				onChange={() => handleStatusToggleChange(params.row)}
				slotProps={{
					input: {
						"aria-label": `${params.value ? "Deactivate" : "Activate"} rule ${params.row.name ?? ""}`.trim(),
					},
				}}
			/>
		)
	},
	{
		field: "name",
		headerName: "Rule name",
		flex: 1,
		valueGetter: (params) => (params === "" || params === null) ? " " : params,
		renderCell: (params) => (
			<RuleDataGridLongTextCell value={params.value} hasBeforeEffect={!params.row.active && params.row.isDraft} />
		)
	},
	{
		field: "platform",
		headerName: "Platform",
		flex: 1,
		sortable: true,
		valueGetter: (value: RuleLibraryGridRow["platform"]) => value?.name,
		renderCell: (params) => (
			<RuleDataGridLongTextCell value={params.row.platform?.name || " "} />
		)
	},
	{
		field: "description",
		headerName: "Description",
		flex: 1,
		sortable: true,
		valueGetter: (params) => (params === "" || params === null) ? " " : params,
		renderCell: (params) => (
			<RuleDataGridLongTextCell value={params.value} />
		)
	},
	{
		field: "groups",
		headerName: "Groups",
		flex: 1,
		sortable: false,
		renderCell: (params) => (
			<CoAppDataGridListCell items={params.value} resource="Groups" />
		)
	},
	{
		field: "tags",
		headerName: "Tags",
		minWidth: 240,
		flex: 1.5,
		sortable: false,
		renderCell: (params) => (
			handleAddTagToRule && handleRemoveTagFromRule ? (
				<CoAppInlineTagsCell
					tags={params.value || []}
					onAddTag={(tag) => handleAddTagToRule(params.row, tag)}
					onRemoveTag={(tag) => handleRemoveTagFromRule(params.row, tag)}
				/>
			) : (
				<CoAppDataGridListCell items={params.value} resource="Tags" />
			)
		)
	},
	{
		field: "createdAt",
		headerName: "Created",
		width: 175,
		type: "dateTime",
		valueGetter: (params) => !params ? null : new Date(params),
		sortable: true,
		renderCell: (params) => (
			<CoAppDataGridDateTimeCell value={params.value} />
		),
	},
	{
		field: "updatedAt",
		headerName: "Updated",
		width: 175,
		type: "dateTime",
		valueGetter: (params) => !params ? null : new Date(params),
		sortable: true,
		renderCell: (params) => (
			<CoAppDataGridDateTimeCell value={params.value} />
		),
	},
	{
		field: "lastCompiled",
		headerName: "Last Compiled",
		width: 175,
		type: "dateTime",
		valueGetter: (params) => !params ? null : new Date(params),
		sortable: true,
		renderCell: (params) => (
			<CoAppDataGridDateTimeCell value={params.value} />
		),
	},
	{
		field: "actions",
		type: "actions",
		width: 130,
		sortable: false,
		filterable: false,
		hideable: false,
		headerName: "",
		align: "right",
		headerAlign: "right",
		renderCell: (params) => (
			<RowActionIcons
				onEdit={() => {
					const id = params.row.id;
					if (id !== undefined && id !== null) handleEditRuleClick(id);
				}}
				onDuplicate={() => {
					const id = params.row.id;
					if (id !== undefined && id !== null) handleCopyRuleClick(id);
				}}
				onDelete={() => handleDeleteRuleToggle(params.row)}
			/>
		)
	}
];
